import { ListPage } from "@services/common/types";
import API, { ListOptions, QueryTypeOption } from "@services/root/rootApi";
import {
  Account,
  AccountFilters,
  Channel,
  IssueName,
  AuthenticationConfiguration,
  SAMLConfigurationBinding,
} from "./types";
import { Dispatch, SetStateAction } from "react";

type GetAccountsOptions = QueryTypeOption & ListOptions;

export const getAccounts = async (
  filters?: AccountFilters,
  options?: GetAccountsOptions
): Promise<ListPage<Omit<Account, "details">>> => {
  const {
    maxResults,
    orderBy = "name asc",
    startIndex,
    totalLimit,
    queryType = "monitoring",
  } = options ?? {};

  const excludeSimpleAssets = filters?.excludeSimpleAssets;
  delete filters?.["excludeSimpleAssets"];

  const response = await API.post<ListPage<Account>>(
    `/symphony-api/${queryType}/account`,
    filters,
    {
      params: {
        maxResults,
        orderBy,
        startIndex,
        totalLimit,
        excludeSimpleAssets: excludeSimpleAssets,
      },
    }
  );
  return response.data;
};
export const getUserAccounts = async (userId: string): Promise<Account[]> => {
  const response = await API.get<Account[]>(
    `/symphony-api/provisioning/user/${userId}/accounts`
  );
  return response.data;
};

export const deleteAccounts = async (ids: Account["id"][]) =>
  await API.delete(`/symphony-api/provisioning/account`, { data: ids });

export const getChannels = async (): Promise<Channel[]> =>
  (await API.post<Channel[]>(`/symphony-api/provisioning/channel`)).data;
export const putChannel = async (name: string) =>
  (await API.put(`/symphony-api/provisioning/channel`, { name })).data;
export const deleteChannels = async (channels: Channel["id"][]) =>
  (await API.delete(`/symphony-api/provisioning/channel`, { data: channels }))
    .data;

export const getAccount = async (accountId: string): Promise<Account> => {
  const response = await API.get(
    `/symphony-api/provisioning/account/${accountId}`
  );
  return response.data;
};

export const putAccount = async (
  account: Omit<Account, "id"> & Partial<Pick<Account, "id">>
) =>
  await API.put<string>(
    `/symphony-api/provisioning/account${
      account.id !== undefined ? `/${account.id}` : ""
    }`,
    account
  );

export const getAccountSummary = async (
  accountId: string
): Promise<Account> => {
  const response = await API.get(
    `/symphony-api/provisioning/account/${accountId}/summary`
  );

  return response.data;
};

export const getLinkableAccounts = async (): Promise<Account[]> => {
  const response = await API.get<Account[]>(
    `/symphony-api/provisioning/user/account/summary`
  );
  return response.data;
};

export const getIssue = async (
  accountId: string,
  name: IssueName
): Promise<string[]> => {
  const response = await API.get(
    `/symphony-api/provisioning/account/${accountId}/issue?name=${name}`
  );
  return response.data?.sort((a: string, b: string) => a.localeCompare(b));
};

export const changeVisibility = async (body: {
  accountId: string[];
  userId?: string[];
  visible: boolean;
  sourceAccountId?: string;
  roleId?: string[];
}) => {
  const response = await API.post(
    `/symphony-api/provisioning/account/visibility`,
    body
  );
  return response.data;
};

export const postBillingCode = async (
  accountId: string,
  data: FormData,
  setProgress: Dispatch<SetStateAction<number>>
): Promise<Account> => {
  const response = await API.post(
    `/symphony-api/provisioning/account/${accountId}/billingcodes`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const progress = (progressEvent.progress ?? 0) * 100;
        setProgress(progress);
      },
    }
  );
  return response.data;
};

export const getAuthentication = async (
  accountId: string
): Promise<AuthenticationConfiguration> => {
  const response = await API.get(
    `/symphony-api/provisioning/account/${accountId}/authentication`
  );
  return response.data;
};

export const putAuthentication = async (
  accountId: string,
  data: AuthenticationConfiguration
) =>
  await API.put<AuthenticationConfiguration>(
    `/symphony-api/provisioning/account/${accountId}/authentication`,
    data
  );

export const postAuthenticationMetadata = async (
  accountId: string,
  registrationId: string,
  data: { binding: SAMLConfigurationBinding; entityId: string }
): Promise<any> => {
  const response = await API.post(
    `/symphony-api/provisioning/account/${accountId}/authentication/${registrationId}/metadata`,
    data
  );
  return response.data;
};

interface CommonFieldsResponse {
  resolution: string[];
  source: string[];
}
export const getCommonFields = async (
  accountIds: Array<Required<Account>["id"]>
): Promise<CommonFieldsResponse> =>
  (
    await API.post<CommonFieldsResponse>(
      `/symphony-api/provisioning/account/issue/commonfields`,
      accountIds
    )
  ).data;
